import React, { useState } from 'react';
import { Button, Empty, Form, Input, Select, Switch } from 'antd';
import { useAgentsQuery, useAgentToolsQuery, useUpdateAgentMutation } from '../../queries/agents';
import { Card, CardHead, Muted } from '../../components';

const E164 = /^\+[1-9]\d{7,14}$/;

export default function AssistantCard() {
  const { data: agents = [], isPending } = useAgentsQuery();
  const { mutate, isPending: saving } = useUpdateAgentMutation();
  const [selectedId, setSelectedId] = useState(null);
  const agent = agents.find((a) => a._id === selectedId) || agents[0];
  const { data: tools = [] } = useAgentToolsQuery(agent?._id);

  const save = ({ tools: on, ...v }) =>
    mutate({
      id: agent._id,
      ...v,
      transferNumber: v.transferNumber?.trim() || '',
      ...(tools.length ? { enabledTools: Object.keys(on || {}).filter((k) => on[k]) } : {})
    });

  return (
    <Card aria-labelledby="assistant-title">
      <CardHead>
        <h2 id="assistant-title">Assistant</h2>
        <span>how your assistant answers</span>
      </CardHead>
      {!isPending && !agent && <Empty description="No assistant set up yet." />}
      {agent && (
        <>
          {agents.length > 1 && (
            <Select
              aria-label="Assistant"
              value={agent._id}
              onChange={setSelectedId}
              options={agents.map((a) => ({ value: a._id, label: a.name }))}
              style={{ width: '100%', marginBottom: 16 }}
            />
          )}
          <Form
            key={`${agent._id}:${tools.length}`}
            layout="vertical"
            onFinish={save}
            requiredMark={false}
            initialValues={{
              name: agent.name,
              greeting: agent.prompt?.greeting || '',
              transferNumber: agent.transfer?.number || '',
              tools: Object.fromEntries(tools.map((t) => [t.key, t.enabled]))
            }}
          >
            <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, max: 60 }]}>
              <Input />
            </Form.Item>
            <Form.Item
              name="greeting"
              label="Greeting"
              tooltip="The first thing callers hear. Leave blank to use the default."
              rules={[{ max: 500 }]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item
              name="transferNumber"
              label="Transfer number"
              tooltip="Where calls go when a caller asks for a person. E.164, e.g. +18175551234"
              rules={[{ pattern: E164, transform: (v) => v?.trim(), message: 'Use E.164 format, e.g. +18175551234' }]}
            >
              <Input placeholder="+18175551234" />
            </Form.Item>
            {tools.map((t) => (
              <Form.Item
                key={t.key}
                name={['tools', t.key]}
                label={t.label}
                valuePropName="checked"
                tooltip={t.description || 'Turn a tool off to keep it out of the assistant.'}
              >
                <Switch />
              </Form.Item>
            ))}
            <Button type="primary" htmlType="submit" loading={saving}>
              Save assistant
            </Button>
          </Form>
        </>
      )}
      <Muted style={{ marginTop: 16 }}>Other assistant settings are managed by our team.</Muted>
    </Card>
  );
}
